// ============================================
// Launch Gate Service (PI-007)
//
// Automated gate check: is the Captain validated?
// Stan can adjust all thresholds.
//
// Firestore: platform/calibration/launch_gate (singleton)
// ============================================

import {
  doc, getDoc, setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type {
  LaunchGateStatus,
  LaunchGateThresholds,
  CalibrationCase,
} from '../../types/labUpload';
import { DECISION_AREAS } from '../../types/clinicalCalibration';
import { DEFAULT_LAUNCH_GATE_THRESHOLDS } from '../../types/labUpload';
import { getCalibrationCases, getCalibrationStats } from '../firestore/calibrationCaseService';
import { computeDemographicDistribution } from './demographicCheck';
import { C_PLATFORM, SC_CALIBRATION, SC_LAUNCH_GATE } from '../../constants/collections';

// ── Firestore refs ──

const gateDoc = () => doc(db, C_PLATFORM, SC_CALIBRATION, SC_LAUNCH_GATE, 'current');
const thresholdsDoc = () => doc(db, C_PLATFORM, SC_CALIBRATION, SC_LAUNCH_GATE, 'thresholds');

// ── Core Functions ──

/**
 * Compute the current launch gate status.
 */
export async function computeLaunchGate(
  brandId: string,
): Promise<LaunchGateStatus> {
  const [cases, stats, thresholds] = await Promise.all([
    getCalibrationCases(brandId),
    getCalibrationStats(brandId),
    getThresholds(),
  ]);

  const reviewed = cases.filter((c) => c.drCReview);
  const demo = computeDemographicDistribution(cases);

  // Compute per-DP scores
  const dpScores = computeDPScores(reviewed);

  const status: LaunchGateStatus = {
    totalCasesReviewed: stats.reviewedCount,
    womenCases35to60: demo.womenCases35to60,
    overallAlignmentScore: stats.avgAlignment,
    dp5Score: dpScores.dp5,
    dp7Score: dpScores.dp7,
    unresolvedHighStake: dpScores.unresolvedHighStake,
    thresholds,
    passing:
      stats.reviewedCount >= thresholds.minCases &&
      demo.womenCases35to60 >= thresholds.minWomenCases &&
      stats.avgAlignment >= thresholds.minOverallAlignment &&
      dpScores.dp5 >= thresholds.minDP5 &&
      dpScores.dp7 >= thresholds.minDP7 &&
      dpScores.unresolvedHighStake <= thresholds.maxUnresolved,
    computedAt: new Date().toISOString(),
  };

  // Persist the computed status
  await setDoc(gateDoc(), {
    ...status,
    _updatedAt: Timestamp.now(),
  });

  return status;
}

/**
 * Get the current thresholds. Returns defaults if not customized.
 */
export async function getThresholds(): Promise<LaunchGateThresholds> {
  const snap = await getDoc(thresholdsDoc());
  if (snap.exists()) {
    return snap.data() as LaunchGateThresholds;
  }
  return { ...DEFAULT_LAUNCH_GATE_THRESHOLDS };
}

/**
 * Update launch gate thresholds (Stan adjusts).
 */
export async function updateThresholds(
  updates: Partial<LaunchGateThresholds>,
): Promise<void> {
  const current = await getThresholds();
  await setDoc(thresholdsDoc(), {
    ...current,
    ...updates,
    _updatedAt: Timestamp.now(),
  });
}

/**
 * Get the last computed launch gate status.
 */
export async function getLastLaunchGateStatus(): Promise<LaunchGateStatus | null> {
  const snap = await getDoc(gateDoc());
  return snap.exists() ? (snap.data() as LaunchGateStatus) : null;
}

// ── Internal Helpers ──

function computeDPScores(reviewed: CalibrationCase[]): {
  dp5: number;
  dp7: number;
  unresolvedHighStake: number;
} {
  let dp5Total = 0, dp5Aligned = 0;
  let dp7Total = 0, dp7Aligned = 0;
  let unresolvedHighStake = 0;

  // Use canonical area names from DECISION_AREAS
  const DP5_AREA = DECISION_AREAS[4].area; // Product Selection
  const DP7_AREA = DECISION_AREAS[6].area; // Missed Signals
  const HIGH_STAKE_AREAS = [DECISION_AREAS[0].area, DECISION_AREAS[3].area]; // Primary Dx, Supplement-First

  for (const c of reviewed) {
    if (!c.decisionPoints) continue;
    for (const dp of c.decisionPoints) {
      if (dp.area === DP5_AREA) {
        dp5Total++;
        if (dp.alignment === 'aligned') dp5Aligned++;
      }
      if (dp.area === DP7_AREA) {
        dp7Total++;
        if (dp.alignment === 'aligned') dp7Aligned++;
      }
      // High-stake: Primary Dx or Supplement-First delta with "no" review
      if (HIGH_STAKE_AREAS.includes(dp.area) && dp.alignment === 'delta') {
        if (c.drCReview?.rating === 'no') {
          unresolvedHighStake++;
        }
      }
    }
  }

  return {
    dp5: dp5Total > 0 ? Math.round((dp5Aligned / dp5Total) * 100) : 0,
    dp7: dp7Total > 0 ? Math.round((dp7Aligned / dp7Total) * 100) : 0,
    unresolvedHighStake,
  };
}
